import React, { useState, useEffect, useRef } from 'react';
import { X, Package, Hash, Tag, Truck, Save, CircleDollarSign } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../services/authService';
import formationService from '../../services/formationService';
import MultiSelectDropdown from '../../components/MultiSelectDropdown/MultiSelectDropdown';
import { normalizeConsommableType } from '../../utils/consommableQuantity';
import './AddConsommableDialog.css';

const getLegacyPopulationFlags = (consommable) => {
  const type = consommable?.type_consommable;
  const hasQuantity = (value) => value !== null && value !== undefined;
  return {
    affects_student: hasQuantity(consommable?.quantity_per_student) || ['ETUDIANT', 'ETUDIANT_FORMATEUR', 'ETUDIANT_FORMATEUR_STAFF'].includes(type),
    affects_trainer: hasQuantity(consommable?.quantity_per_trainer) || ['FORMATEUR', 'ETUDIANT_FORMATEUR', 'ETUDIANT_FORMATEUR_STAFF'].includes(type),
    affects_visitor: hasQuantity(consommable?.quantity_per_visitor) || type === 'VISITEUR',
    affects_staff: hasQuantity(consommable?.quantity_per_staff) || ['STAFF', 'ETUDIANT_FORMATEUR_STAFF'].includes(type)
  };
};

const AddConsommableDialog = ({ isOpen, onClose, onConsommableAdded, consommableToEdit }) => {
  const { t } = useTranslation();
  const isEditing = !!consommableToEdit;
  
  const [formData, setFormData] = useState({
    name: '',
    unit_price: '',
    type_consommable: 'FIXE',
    quantity_fixe: 1,
    quantity_per_student: 1,
    quantity_per_trainer: 1,
    quantity_per_visitor: 1,
    quantity_per_staff: 1,
    affects_student: false,
    affects_trainer: false,
    affects_visitor: false,
    affects_staff: false,
    is_per_day: false,
    supplier: '',
    formations: [],
    devise: '',
    devise_custom: ''
  });
  
  const [formations, setFormations] = useState([]);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const dataLoadedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      dataLoadedRef.current = false;
      return;
    }
    if (dataLoadedRef.current) return;
    dataLoadedRef.current = true;
    
    const loadData = async () => {
      const [formationsRes, fournisseursRes] = await Promise.all([
        formationService.getSessionOptions(),
        apiClient.get('/fournisseurs/all/')
      ]);
      const formationsData = formationsRes || [];
      const fournisseursData = fournisseursRes.data.results || fournisseursRes.data || [];
      setFormations(formationsData);
      setFournisseurs(fournisseursData);
      
      if (consommableToEdit) {
        const isStandardDevise = ['', 'EUR', 'USD', 'MAD', 'GBP', 'AOA'].includes(consommableToEdit.devise);
        // Filter formations to only include valid ones that exist in the database
        const validFormationIds = new Set(formationsData.map(f => f.id));
        const validFormations = (consommableToEdit.formations || []).filter(id => validFormationIds.has(id));
        const typeConsommable = normalizeConsommableType(consommableToEdit.type_consommable);
        const populationFlags = getLegacyPopulationFlags(consommableToEdit);
        setFormData({
          name: consommableToEdit.name || '',
          unit_price: consommableToEdit.unit_price || '',
          type_consommable: typeConsommable,
          quantity_fixe: consommableToEdit.quantity_fixe || 1,
          quantity_per_student: consommableToEdit.quantity_per_student || 1,
          quantity_per_trainer: consommableToEdit.quantity_per_trainer || 1,
          quantity_per_visitor: consommableToEdit.quantity_per_visitor || 1,
          quantity_per_staff: consommableToEdit.quantity_per_staff || 1,
          ...populationFlags,
          is_per_day: !!consommableToEdit.is_per_day,
          supplier: consommableToEdit.supplier || '',
          formations: validFormations,
          devise: isStandardDevise ? (consommableToEdit.devise || '') : 'AUTRE',
          devise_custom: isStandardDevise ? '' : (consommableToEdit.devise || '')
        });
      } else {
        setFormData({
          name: '',
          unit_price: '',
          type_consommable: 'FIXE',
          quantity_fixe: 1,
          quantity_per_student: 1,
          quantity_per_trainer: 1,
          quantity_per_visitor: 1,
          quantity_per_staff: 1,
          affects_student: false,
          affects_trainer: false,
          affects_visitor: false,
          affects_staff: false,
          is_per_day: false,
          supplier: '',
          formations: [],
          devise: '',
          devise_custom: ''
        });
      }
      setError(null);
    };
    
    loadData();
  }, [isOpen, consommableToEdit]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleFormationsChange = (selectedFormations) => {
    setFormData(prev => ({
      ...prev,
      formations: selectedFormations
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.unit_price || !formData.supplier) {
      setError(t('consommables.dialog.errors.required'));
      return;
    }
    if (
      formData.type_consommable === 'VARIABLE' &&
      !formData.affects_student &&
      !formData.affects_trainer &&
      !formData.affects_visitor &&
      !formData.affects_staff
    ) {
      setError(t('consommables.dialog.errors.populationRequired'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        ...formData,
        unit_price: parseFloat(formData.unit_price),
        quantity_fixe: formData.type_consommable === 'FIXE' ? (parseInt(formData.quantity_fixe) || 0) : null,
        quantity_per_student: formData.type_consommable === 'VARIABLE' && formData.affects_student
          ? (parseInt(formData.quantity_per_student) || 0)
          : null,
        quantity_per_trainer: formData.type_consommable === 'VARIABLE' && formData.affects_trainer
          ? (parseInt(formData.quantity_per_trainer) || 0)
          : null,
        quantity_per_visitor: formData.type_consommable === 'VARIABLE' && formData.affects_visitor
          ? (parseInt(formData.quantity_per_visitor) || 0)
          : null,
        quantity_per_staff: formData.type_consommable === 'VARIABLE' && formData.affects_staff
          ? (parseInt(formData.quantity_per_staff) || 0)
          : null,
        is_per_day: !!formData.is_per_day,
        devise: formData.devise === 'AUTRE' ? formData.devise_custom.toUpperCase() : (formData.devise || ''),
      };
      delete payload.devise_custom;
      delete payload.affects_student;
      delete payload.affects_trainer;
      delete payload.affects_visitor;
      delete payload.affects_staff;

      if (isEditing) {
        await apiClient.put(`/consommables/${consommableToEdit.id}/`, payload);
      } else {
        await apiClient.post('/consommables/', payload);
      }
      
      onConsommableAdded();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || t('consommables.dialog.errors.save', { action: isEditing ? t('consommables.dialog.actions.editing') : t('consommables.dialog.actions.creating') }));
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  return (
    <div className="dialog-overlay ui-dialog-overlay" onClick={handleOverlayClick}>
      <div className="dialog-container ui-dialog-container">
        <div className="dialog-header ui-dialog-header">
          <div className="dialog-title ui-dialog-title">
            <Package size={24} />
            <h2>{isEditing ? t('consommables.dialog.editTitle') : t('consommables.dialog.addTitle')}</h2>
          </div>
          <button className="dialog-close" onClick={onClose} disabled={loading}>
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="dialog-error">
            {error}
          </div>
        )}

        <form className="dialog-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name" className="ui-label">
              <Package size={16} />
              {t('consommables.dialog.form.name')} *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={t('consommables.dialog.form.namePlaceholder')}
              required
              className="ui-input"
            />
          </div>

          <div className="form-group">
            <MultiSelectDropdown
              label={t('consommables.dialog.form.formationsOptional')}
              icon={Tag}
              options={formations.map(f => ({ value: f.id, label: f.title }))}
              selectedValues={formData.formations}
              onChange={handleFormationsChange}
              placeholder={t('consommables.dialog.form.formationsPlaceholder')}
            />
          </div>

          <div className="form-section">
            <div className="form-section-title">{t('consommables.dialog.form.purchaseInfo')}</div>
            <div className="form-row">
            <div className="form-group">
              <label htmlFor="unit_price" className="ui-label">
                {t('consommables.dialog.form.unitPrice')}  *
              </label>
              <input
                type="number"
                id="unit_price"
                name="unit_price"
                value={formData.unit_price}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
                min="0"
                required
                className="ui-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="devise" className="ui-label">
                <CircleDollarSign size={16} />
                {t('consommables.dialog.form.devise')}
              </label>
              <select
                id="devise"
                name="devise"
                value={formData.devise}
                onChange={handleChange}
                className="form-select ui-select"
              >
                <option value="">{t('consommables.dialog.form.devisePlaceholder')}</option>
                <option value="EUR">EUR (€)</option>
                <option value="USD">USD ($)</option>
                <option value="MAD">MAD (DH)</option>
                <option value="GBP">GBP (£)</option>
                <option value="AOA">AOA (Kz)</option>
                <option value="AUTRE">{t('consommables.dialog.form.deviseOther')}</option>
              </select>
            </div>
          </div>

            {formData.devise === 'AUTRE' && (
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="devise_custom" className="ui-label">
                  <CircleDollarSign size={16} />
                  {t('consommables.dialog.form.deviseCustom')}
                </label>
                <input
                  type="text"
                  id="devise_custom"
                  name="devise_custom"
                  value={formData.devise_custom}
                  onChange={handleChange}
                  placeholder={t('consommables.dialog.form.deviseCustomPlaceholder')}
                  maxLength={3}
                  className="ui-input"
                />
              </div>
            </div>
            )}

            <div className="form-group">
              <label htmlFor="supplier" className="ui-label">
                <Truck size={16} />
                {t('consommables.dialog.form.supplier')} *
              </label>
              <select
                id="supplier"
                name="supplier"
                value={formData.supplier}
                onChange={handleChange}
                required
                className="form-select ui-select"
              >
                <option value="">{t('consommables.dialog.form.supplierPlaceholder')}</option>
                {fournisseurs.map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-section">
            <div className="form-section-title">{t('consommables.dialog.form.calculationRule')}</div>
            <div className="form-row align-end">
              <div className="form-group">
                <label htmlFor="type_consommable" className="ui-label">
                  <Tag size={16} />
                  {t('consommables.dialog.form.type')} *
                </label>
                <select
                  id="type_consommable"
                  name="type_consommable"
                  value={formData.type_consommable}
                  onChange={handleChange}
                  required
                  className="form-select ui-select"
                >
                  <option value="FIXE">{t('consommables.types.FIXE')}</option>
                  <option value="VARIABLE">{t('consommables.types.VARIABLE')}</option>
                </select>
              </div>

              <div className="form-group compact-toggle">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_per_day"
                    checked={!!formData.is_per_day}
                    onChange={handleCheckboxChange}
                  />
                  <span>{t('consommables.dialog.form.perDay')}</span>
                </label>
              </div>
            </div>

            {formData.type_consommable === 'FIXE' ? (
              <div className="form-group fixed-quantity">
                <label htmlFor="quantity_fixe" className="ui-label">
                  <Hash size={16} />
                  {t('consommables.dialog.form.fixedQuantity')} *
                </label>
                <input
                  type="number"
                  id="quantity_fixe"
                  name="quantity_fixe"
                  value={formData.quantity_fixe}
                  onChange={handleChange}
                  min="1"
                  required
                  className="ui-input"
                />
              </div>
            ) : null}

            {formData.type_consommable === 'VARIABLE' && (
              <div className="population-panel">
                <label className="ui-label">
                  <Tag size={16} />
                  {t('consommables.dialog.form.impactedPopulations')} *
                </label>
                <div className="population-options">
                  <label className="checkbox-label">
                    <input type="checkbox" name="affects_student" checked={!!formData.affects_student} onChange={handleCheckboxChange} />
                    <span>{t('consommables.dialog.form.affectsStudent')}</span>
                  </label>
                  <label className="checkbox-label">
                    <input type="checkbox" name="affects_trainer" checked={!!formData.affects_trainer} onChange={handleCheckboxChange} />
                    <span>{t('consommables.dialog.form.affectsTrainer')}</span>
                  </label>
                  <label className="checkbox-label">
                    <input type="checkbox" name="affects_staff" checked={!!formData.affects_staff} onChange={handleCheckboxChange} />
                    <span>{t('consommables.dialog.form.affectsStaff')}</span>
                  </label>
                  <label className="checkbox-label">
                    <input type="checkbox" name="affects_visitor" checked={!!formData.affects_visitor} onChange={handleCheckboxChange} />
                    <span>{t('consommables.dialog.form.affectsVisitor')}</span>
                  </label>
                </div>
              </div>
            )}

            {formData.type_consommable === 'VARIABLE' && (
              <div className="quantity-grid">
                {formData.affects_student ? (
                  <div className="form-group">
                    <label htmlFor="quantity_per_student" className="ui-label">
                      <Hash size={16} />
                      {t('consommables.dialog.form.quantityPerStudent')} *
                    </label>
                    <input
                      type="number"
                      id="quantity_per_student"
                      name="quantity_per_student"
                      value={formData.quantity_per_student}
                      onChange={handleChange}
                      min="1"
                      required
                      className="ui-input"
                    />
                  </div>
                ) : null}

                {formData.affects_trainer ? (
                  <div className="form-group">
                    <label htmlFor="quantity_per_trainer" className="ui-label">
                      <Hash size={16} />
                      {t('consommables.dialog.form.quantityPerTrainer')} *
                    </label>
                    <input
                      type="number"
                      id="quantity_per_trainer"
                      name="quantity_per_trainer"
                      value={formData.quantity_per_trainer}
                      onChange={handleChange}
                      min="1"
                      required
                      className="ui-input"
                    />
                  </div>
                ) : null}

                {formData.affects_staff ? (
                  <div className="form-group">
                    <label htmlFor="quantity_per_staff" className="ui-label">
                      <Hash size={16} />
                      {t('consommables.dialog.form.quantityPerStaff')} *
                    </label>
                    <input
                      type="number"
                      id="quantity_per_staff"
                      name="quantity_per_staff"
                      value={formData.quantity_per_staff}
                      onChange={handleChange}
                      min="1"
                      required
                      className="ui-input"
                    />
                  </div>
                ) : null}

                {formData.affects_visitor ? (
                  <div className="form-group">
                    <label htmlFor="quantity_per_visitor" className="ui-label">
                      <Hash size={16} />
                      {t('consommables.dialog.form.quantityPerVisitor')} *
                    </label>
                    <input
                      type="number"
                      id="quantity_per_visitor"
                      name="quantity_per_visitor"
                      value={formData.quantity_per_visitor}
                      onChange={handleChange}
                      min="1"
                      required
                      className="ui-input"
                    />
                  </div>
                ) : null}
              </div>
            )}
          </div>

          <div className="dialog-actions ui-dialog-actions">
            <button
              type="button"
              className="btn-secondary ui-btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary ui-btn-primary"
              disabled={loading}
            >
              {loading ? t('common.saving') : (
                <>
                  <Save size={18} />
                  {isEditing ? t('common.edit') : t('consommables.dialog.actions.create')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddConsommableDialog;
